/**
 * Display-side currency formatter for Czech CZK amounts.
 *
 * Produces abbreviated forms for UI display only:
 * - `< 1 000`             → `850 Kč` (whole Kč, no decimal)
 * - `1 000 – < 100 000`   → `<n>[,<d>] tis. Kč` (1 decimal only when non-whole)
 * - `100 000 – < 1 000 000` → `<n> tis. Kč` (always whole — extra decimal would
 *   be noise on a 6-digit raw value)
 * - `≥ 1 000 000`         → `<n>[,<d>] mil. Kč` (up to 2 decimals when non-whole;
 *   trailing zeros dropped, so `1,50 mil.` is rendered as `1,5 mil.`)
 * - One space between the abbreviated number and the unit.
 *
 * Domain calculations stay in whole-CZK `number` precision. This helper
 * only formats for display.
 */
export function formatCzkShort(value: number): string {
  if (Number.isNaN(value) || !Number.isFinite(value) || value < 0) {
    return '0 Kč';
  }

  if (value < 1000) {
    return `${formatThousands(Math.round(value))} Kč`;
  }

  if (value < 1_000_000) {
    const thousands = value / 1000;
    if (Math.round(thousands) >= 1000) {
      return formatMillions(thousands / 1000);
    }
    const wholeOnly = value >= 100_000;
    return `${formatAbbreviated(thousands, wholeOnly ? 0 : 1)} tis. Kč`;
  }

  return formatMillions(value / 1_000_000);
}

function formatMillions(millions: number): string {
  return `${formatAbbreviated(millions, 2)} mil. Kč`;
}

function formatAbbreviated(value: number, maxDecimals: number): string {
  const factor = Math.pow(10, maxDecimals);
  const rounded = Math.round(value * factor) / factor;
  return rounded % 1 === 0
    ? formatThousands(rounded)
    : rounded.toFixed(maxDecimals).replace(/\.?0+$/, '').replace('.', ',');
}

/**
 * Strips the trailing currency unit (`" Kč"`, `" tis."`, `" mil."`) from a
 * value formatted by `formatCzkShort`, so a caller can pass the abbreviated
 * number (`"165 tis."`) and supply the unit (`"Kč / měs."`) separately.
 */
export function stripCzkShortUnit(formatted: string): string {
  for (const suffix of [' Kč', ' tis.', ' mil.']) {
    if (formatted.endsWith(suffix)) {
      return formatted.substring(0, formatted.length - suffix.length);
    }
  }
  return formatted;
}


function formatThousands(value: number): string {
  const str = String(Math.round(value));
  let buf = '';
  for (let i = 0; i < str.length; i++) {
    if (i > 0 && (str.length - i) % 3 === 0) buf += ' ';
    buf += str[i];
  }
  return buf;
}
